import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { ButtonWithIcon } from '@els/els-react--button';
import bannerBackground from '@assets/avif/Banner-CondNav-1026.avif';
import { trackDrawLearnHowItWorks, trackDrawLearnPanelExample } from '../analytics';
import { insertSmilesIntoEditor } from '../insertStructure';
import ConditionsLearnPanel from './ConditionsLearnPanel';
import './ConditionsBanner.scss';

function ConditionsBanner({ editorInstanceRef = null, isLearnPanelInitiallyOpen = false }) {
  // Seeded once on mount rather than tracked as a prop: the flag behind it (router state
  // `noProjects`) has to stay on the location for the whole visit, because ConditionsDraw reads it
  // live to keep "Cancel" disabled. Deriving open/closed from it on every render would make the
  // panel un-closable, so the prop is only an initial value and the user owns it from then on.
  //
  // That auto-open stays out of analytics on purpose: trackDrawLearnHowItWorks below is a
  // buttonClick event, so firing it here would count visits nobody clicked through.
  const [isPanelOpen, setIsPanelOpen] = useState(isLearnPanelInitiallyOpen);

  const openPanelHandler = () => {
    trackDrawLearnHowItWorks();
    setIsPanelOpen(true);
  };

  // The example reactions load into the same editor the user is drawing in, which the panel covers,
  // so closing it is part of the action rather than a separate step. The insert itself no-ops while
  // the editor is still booting, so the links stay clickable rather than flickering in and out.
  const exampleClickHandler = (example) => {
    trackDrawLearnPanelExample(example.key);
    insertSmilesIntoEditor(editorInstanceRef, example.smiles);
    setIsPanelOpen(false);
  };

  return (
    <section className="conditions-banner" aria-labelledby="conditions-banner-title">
      <img src={bannerBackground} alt="" aria-hidden="true" className="conditions-banner__image" decoding="async" loading="eager" />
      <div className="conditions-banner__content">
        <h1 id="conditions-banner-title" className="conditions-banner__title u-els-font-family-serif u-els-color-white">
          Reaxys <span className="u-els-font-family-serif-italic">Conditions Navigator</span>
        </h1>
        <ButtonWithIcon
          data-e2e="conditions-banner-learn"
          className="conditions-banner__link u-els-font-size-h3"
          type="link"
          sprite="ChevronRight"
          iconRight
          onClick={openPanelHandler}>
          Learn how it works
        </ButtonWithIcon>
      </div>
      {isPanelOpen && <ConditionsLearnPanel onClose={() => setIsPanelOpen(false)} onExampleClick={exampleClickHandler} />}
    </section>
  );
}

ConditionsBanner.propTypes = {
  /** Ref to the draw page's structure editor, owned by Conditions.jsx. Enables the example links. */
  editorInstanceRef: PropTypes.shape({ current: PropTypes.any }),
  /** Opens the learn panel on mount. Set for users bounced here because they have no projects. */
  isLearnPanelInitiallyOpen: PropTypes.bool,
};

export default ConditionsBanner;
